import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";
import superjson from "superjson";
import { initSentry, Sentry } from "./lib/sentry";
import { trpc } from "./lib/trpc";
import { AuthProvider } from "./lib/auth";
import { Toaster } from "./components/ui/sonner";
import App from "./App";
import "./index.css";

initSentry();

// Auto-recover from stale chunk errors after a deploy. Vite fires
// `vite:preloadError` when a lazy import 404s; also catch the generic
// "Failed to fetch dynamically imported module" TypeError. Reload once
// (guarded by sessionStorage so a persistent failure doesn't loop).
const reloadOnce = () => {
  const key = "fygg:chunk-reload-at";
  const last = Number(sessionStorage.getItem(key) || 0);
  if (Date.now() - last < 10_000) return;
  sessionStorage.setItem(key, String(Date.now()));
  window.location.reload();
};
window.addEventListener("vite:preloadError", (e) => {
  e.preventDefault();
  reloadOnce();
});
window.addEventListener("error", (e) => {
  const msg = e.message || "";
  if (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  ) {
    reloadOnce();
  }
});
window.addEventListener("unhandledrejection", (e) => {
  const msg = (e.reason as Error | undefined)?.message || "";
  if (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  ) {
    reloadOnce();
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
    }),
  ],
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={
        <div className="min-h-screen flex items-center justify-center p-6">
          <div className="max-w-md text-center space-y-3">
            <h1 className="text-lg font-semibold">Something broke</h1>
            <p className="text-sm text-muted-foreground">
              The error has been reported. Try reloading the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="text-sm underline"
            >
              Reload
            </button>
          </div>
        </div>
      }
    >
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <App />
              <Toaster richColors position="bottom-right" />
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </trpc.Provider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
